import { UnblockButton, InputStepUser } from "~/components/Playground/Node/InputStepNode";
import {
  BlockStep,
  InputStep,
  Outcome,
  State,
  hasReceivedInput,
  isCanceled,
  isInputDisabled,
  isIgnored,
} from "~/lib/pipeline";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import Icon from "~/components/shared/Icon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import { twMerge } from "tailwind-merge";
import Emojify from "~/components/shared/Emojify";
import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import {
  StepLink,
  stepLinkClassName,
} from "~/components/build/Show/routes/list/-components/TreeNode/StepLink";
import { STEP_ITEM_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import { TreeNode } from "~/components/build/Show/routes/list/-components/TreeNode";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { StepRow } from "~/components/build/Show/routes/list/-components/StepRow";
import { Badge } from "~/components/build/Show/routes/list/-components/Badge";

interface Props {
  step: InputStep | BlockStep;
  depth?: number;
}

export function InputStepItem({ step, depth = 0 }: Props) {
  const { currentStep } = useCurrentJobOrStep();
  const { missingDependencies } = useStepDependencies(step.uuid);

  const isStepIgnored = isIgnored(step);
  const url = resolveStepUrl(step.uuid, { open: false });
  const label = step.label || "Continue";

  if (isStepIgnored) {
    return (
      <StepLink
        state={step.state}
        to={url}
        active={step.uuid === currentStep?.uuid}
        depth={depth}
        title="Skipped steps cannot be viewed"
      >
        <StepRow
          depth={depth}
          stepType={step.type}
          icon={
            <StateIcon
              state={step.state}
              outcome={step.outcome}
              missingDependencies={missingDependencies}
              className={textColorForStep({ ...step, missingDependencies })}
            />
          }
          label={<Emojify text={label} />}
        />
      </StepLink>
    );
  }

  if (hasReceivedInput(step)) {
    const actorName = step.actor?.name || "someone";

    return (
      <StepLink state={step.state} to={url} active={step.uuid === currentStep?.uuid} depth={depth}>
        <StepRow
          depth={depth}
          stepType={step.type}
          icon={
            <StateIcon
              state={State.Finished}
              outcome={Outcome.Passed}
              missingDependencies={missingDependencies}
              className={twMerge(
                textColorForStep({
                  state: State.Finished,
                  outcome: Outcome.Passed,
                  missingDependencies,
                }),
              )}
            />
          }
          label={<Emojify text={label} />}
          meta={
            <Badge
              className="inline-flex items-center gap-1 whitespace-nowrap"
              title={`Unblocked by ${actorName}`}
            >
              <InputStepUser step={step} size="MICRO" />
              <span>
                <span className="container-hidden @xl/list:inline">Unblocked by </span>
                {actorName}
              </span>
            </Badge>
          }
        />
      </StepLink>
    );
  }

  const inputRequiredBadge =
    isCanceled(step) || isInputDisabled(step) ? (
      <Badge className="opacity-50">Input required</Badge>
    ) : (
      <Badge className="bg-purple-100 text-purple-600">Input required</Badge>
    );

  const inputIcon =
    isCanceled(step) || isInputDisabled(step) ? (
      <StateIcon
        state={step.state}
        outcome={step.outcome}
        missingDependencies={missingDependencies}
        className={textColorForStep({ ...step, missingDependencies })}
      />
    ) : (
      <Icon
        icon="paused-tiny"
        aria-label="Waiting for input"
        style={{ fill: "none" }}
        className={twMerge("h-4 w-4", textColorForStep({ ...step, missingDependencies }))}
      />
    );

  const stepRow = (
    <StepRow
      depth={depth}
      stepType={step.type}
      icon={inputIcon}
      label={
        <span className="inline-flex min-w-0 items-center gap-2">
          <span className="truncate">
            <Emojify text={label} />
          </span>
          <span className="shrink-0">{inputRequiredBadge}</span>
        </span>
      }
    />
  );

  if (isCanceled(step) || isInputDisabled(step)) {
    return (
      <StepLink
        state={step.state}
        to={url}
        active={step.uuid === currentStep?.uuid}
        depth={depth}
        disabled={true}
        className={isInputDisabled(step) ? "cursor-not-allowed" : undefined}
      >
        {stepRow}
      </StepLink>
    );
  }

  return (
    <UnblockButton step={step} className={stepLinkClassName({ depth })} style={{ height: "100%" }}>
      {stepRow}
    </UnblockButton>
  );
}

export const createInputStepNode = (step: InputStep | BlockStep): TreeNode => ({
  id: step.uuid,
  height: STEP_ITEM_HEIGHT,
  render: ({ depth }) => <InputStepItem step={step} depth={depth} />,
});
